import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import IntegrationActions from '../IntegrationActions';
import { asList, budgetLineAmount, costCodeLabel, costCodeVuidsFrom, costTypeLabel, deleteJson, getJson, integrationError, loadCostLookups, money, periodLabel, postJson, putJson, shortDate, statusClass } from '../helpers';
import { feeLevelLabel, usesFeeCostCodeLine, usesLineMarkups } from '../../utils/contractFees';

export default function RecordDetailPage({ parentLabel, parentTo, loader }) {
  const { projectVuid } = useParams();
  const navigate = useNavigate();
  const [projectName, setProjectName] = useState('');
  const [record, setRecord] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [periodVuid, setPeriodVuid] = useState('');
  const [periodBusy, setPeriodBusy] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [flash, setFlash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    setFlash(null);
    Promise.all([
      loader(),
      projectVuid ? getJson(`/api/projects/${projectVuid}`).catch(() => null) : Promise.resolve(null),
    ])
      .then(async ([data, project]) => {
        if (cancelled) return;
        setRecord(data);
        if (project?.project_name) setProjectName(project.project_name);
        if (data?.period) {
          const periodPayload = await getJson('/api/accounting-periods').catch(() => []);
          if (cancelled) return;
          const periodList = sortPeriods(asList(periodPayload));
          if (
            data.period.accounting_period?.vuid &&
            !periodList.some((period) => period.vuid === data.period.accounting_period.vuid)
          ) {
            periodList.unshift(data.period.accounting_period);
          }
          setPeriods(periodList);
          setPeriodVuid(data.period.accounting_period_vuid || data.period.accounting_period?.vuid || '');
        } else {
          setPeriods([]);
          setPeriodVuid('');
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.error || 'Unable to load this record');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectVuid, parentTo]);

  if (loading) {
    return <div className="pc-page"><div className="pc-empty">Loading record…</div></div>;
  }
  if (error || !record) {
    return <div className="pc-page"><div className="pc-empty">{error || 'Record not found'}</div></div>;
  }

  const selectedPeriod = periods.find((period) => period.vuid === periodVuid);
  const periodLocked = selectedPeriod?.status === 'closed';
  const handlePeriodChange = async (nextPeriodVuid) => {
    if (!record.period?.saveUrl || !nextPeriodVuid || nextPeriodVuid === periodVuid || periodLocked) return;
    setPeriodBusy(true);
    setFlash(null);
    try {
      await putJson(record.period.saveUrl, { accounting_period_vuid: nextPeriodVuid });
      setPeriodVuid(nextPeriodVuid);
      setFlash({ type: 'ok', text: 'Accounting period saved.' });
    } catch (err) {
      setFlash({ type: 'err', text: integrationError(err) });
    } finally {
      setPeriodBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!record.deleteUrl || deleteBusy) return;
    const message =
      record.deleteConfirm ||
      'Unsync this record in Procore (remove origin_id) and delete it from Claret? This cannot be undone.';
    if (!window.confirm(message)) return;
    setDeleteBusy(true);
    setFlash(null);
    try {
      await deleteJson(record.deleteUrl);
      navigate(parentTo);
    } catch (err) {
      setFlash({ type: 'err', text: integrationError(err) });
      setDeleteBusy(false);
    }
  };

  return (
    <div className="pc-page">
      <div className="pc-crumb">
        {projectVuid ? (
          <>
            <Link to="/procore">Portfolio</Link>
            {' / '}
            <Link to={`/procore/projects/${projectVuid}`}>{projectName || 'Project'}</Link>
            {' / '}
            <Link to={parentTo}>{parentLabel}</Link>
            {' / '}
            {record.title}
          </>
        ) : (
          <>
            <Link to={parentTo}>{parentLabel}</Link>
            {' / '}
            {record.title}
          </>
        )}
      </div>
      <div className="pc-title-row">
        <h1>{record.title}</h1>
        <div className="pc-title-actions">
          {record.status ? (
            <span className={`pc-pill ${statusClass(record.status)}`}>{record.status}</span>
          ) : null}
          {record.sendKind ? (
            <IntegrationActions
              projectVuid={projectVuid}
              sendKind={record.sendKind}
              sendPayload={record.sendPayload}
            />
          ) : null}
          {record.deleteUrl ? (
            <button
              type="button"
              className="pc-btn pc-btn-secondary"
              disabled={deleteBusy}
              onClick={handleDelete}
            >
              {deleteBusy ? 'Deleting…' : 'Delete'}
            </button>
          ) : null}
        </div>
      </div>
      {flash ? (
        <div className={`pc-flash ${flash.type === 'ok' ? 'pc-flash-ok' : 'pc-flash-err'}`}>{flash.text}</div>
      ) : null}
      <div className="pc-card">
        {record.period || record.fields?.length ? (
          <div className="pc-kv">
            {record.period ? (
              <div>
                <div className="pc-label">Accounting Period</div>
                <div className="pc-value">
                  {record.period.saveUrl && !periodLocked ? (
                    <select
                      className="pc-select"
                      value={periodVuid}
                      disabled={periodBusy}
                      aria-label="Accounting period"
                      onChange={(event) => handlePeriodChange(event.target.value)}
                    >
                      {!periodVuid ? <option value="">Select a period</option> : null}
                      {periods.map((period) => (
                        <option
                          key={period.vuid}
                          value={period.vuid}
                          disabled={period.status !== 'open' && period.vuid !== periodVuid}
                        >
                          {periodLabel(period)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    periodLabel(selectedPeriod || record.period.accounting_period)
                  )}
                </div>
              </div>
            ) : null}
            {(record.fields || []).map((field) => (
              <div key={field.label}>
                <div className="pc-label">{field.label}</div>
                <div className="pc-value">
                  {field.status ? (
                    <span className={`pc-pill ${statusClass(field.value)}`}>{field.value || '—'}</span>
                  ) : field.money ? (
                    money(field.value)
                  ) : field.date ? (
                    shortDate(field.value)
                  ) : (
                    field.value || '—'
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : null}
        <div className="pc-panel-h">{record.lineTitle || 'Line items'}</div>
        {!record.lines?.length ? (
          <div className="pc-empty">No line items on this record.</div>
        ) : (
          <table className="pc-table">
            <thead>
              <tr>
                {(record.lineColumns || []).map((column) => (
                  <th key={column.key} className={column.money ? 'pc-right' : undefined}>
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {record.lines.map((line, index) => (
                <tr key={line.id || index}>
                  {(record.lineColumns || []).map((column) => (
                    <td key={column.key} className={column.money ? 'pc-right' : undefined}>
                      {column.money
                        ? money(line[column.key])
                        : column.date
                          ? shortDate(line[column.key])
                          : line[column.key] || '—'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export function CommitmentDetail() {
  const { projectVuid, recordVuid } = useParams();
  return (
    <RecordDetailPage
      parentLabel="Commitments"
      parentTo={`/procore/projects/${projectVuid}/commitments`}
      loader={async () => {
        const data = await getJson(`/api/commitments/${recordVuid}`);
        const lines = data.line_items || data.commitment_lines || [];
        const { codeMap, typeMap } = await loadCostLookups(projectVuid, costCodeVuidsFrom(lines));
        return {
          title: `${data.commitment_number || ''} ${data.commitment_name || 'Commitment'}`.trim(),
          status: data.status,
          deleteUrl: `/api/project-commitments/${recordVuid}`,
          deleteConfirm: `Unsync "${data.commitment_number || 'this commitment'}" in Procore (remove origin_id) and delete it from Claret? This cannot be undone.`,
          fields: [
            { label: 'Number', value: data.commitment_number },
            { label: 'Vendor', value: data.vendor?.vendor_name || data.vendor_name },
            { label: 'Original Amount', value: data.original_amount, money: true },
            { label: 'Change Orders', value: data.change_orders_amount, money: true },
            { label: 'Current Amount', value: data.commitment_amount, money: true },
            { label: 'Retainage', value: data.retainage_percentage != null ? `${data.retainage_percentage}%` : '—' },
            { label: 'Date', value: data.commitment_date, date: true },
          ],
          lineTitle: 'Schedule of Values',
          lineColumns: [
            { key: 'description', label: 'Description' },
            { key: 'cost_code', label: 'Cost Code' },
            { key: 'cost_type', label: 'Cost Type' },
            { key: 'change_order_number', label: 'CCO' },
            { key: 'quantity', label: 'Qty' },
            { key: 'unit_cost', label: 'Unit Cost', money: true },
            { key: 'total_cost', label: 'Amount', money: true },
          ],
          lines: lines.map((line) => ({
            id: line.vuid,
            description: line.description,
            cost_code: costCodeLabel(line, codeMap),
            cost_type: costTypeLabel(line, typeMap),
            change_order_number: line.change_order_number,
            quantity: line.quantity,
            unit_cost: line.unit_cost,
            total_cost: line.total_cost,
          })),
        };
      }}
    />
  );
}

export function ApInvoiceDetail() {
  const { projectVuid, recordVuid } = useParams();
  return (
    <RecordDetailPage
      parentLabel="Invoicing"
      parentTo={`/procore/projects/${projectVuid}/invoicing`}
      loader={async () => {
        const [invoice, linePayload] = await Promise.all([
          getJson(`/api/ap-invoices/${recordVuid}`),
          getJson(`/api/ap-invoices/${recordVuid}/line-items`).catch(() => []),
        ]);
        const lines = asList(linePayload).length ? asList(linePayload) : (invoice.line_items || []);
        return {
          title: invoice.invoice_number || 'AP Invoice',
          status: invoice.status,
          sendKind: 'direct-cost',
          sendPayload: { source_type: 'ap_invoice', source_vuid: recordVuid },
          fields: [
            { label: 'Vendor', value: invoice.vendor_name || invoice.vendor?.vendor_name },
            { label: 'Invoice Date', value: invoice.invoice_date, date: true },
            { label: 'Due Date', value: invoice.due_date, date: true },
            { label: 'Subtotal', value: invoice.subtotal, money: true },
            { label: 'Retention Held', value: invoice.retention_held, money: true },
            { label: 'Total', value: invoice.total_amount, money: true },
          ],
          lineTitle: 'Invoice lines',
          lineColumns: [
            { key: 'description', label: 'Description' },
            { key: 'quantity', label: 'Qty' },
            { key: 'unit_cost', label: 'Unit Cost', money: true },
            { key: 'total_amount', label: 'Amount', money: true },
          ],
          lines: lines.map((line) => ({
            id: line.vuid,
            description: line.description,
            quantity: line.quantity,
            unit_cost: line.unit_cost || line.unit_price,
            total_amount: line.total_amount || line.total_cost || line.amount,
          })),
        };
      }}
    />
  );
}

function sortPeriods(periods) {
  return [...periods].sort((a, b) => {
    if (a.status === 'open' && b.status !== 'open') return -1;
    if (a.status !== 'open' && b.status === 'open') return 1;
    if (a.year !== b.year) return b.year - a.year;
    return b.month - a.month;
  });
}

async function billingIsPosted(projectVuid, recordVuid, periodVuid) {
  if (!periodVuid) return false;
  const data = await getJson('/api/posted-records', {
    accounting_period_vuid: periodVuid,
    project_vuid: projectVuid,
  }).catch(() => ({ posted_records: [] }));
  return asList(data?.posted_records).some(
    (row) => row.transaction_vuid === recordVuid && row.transaction_type === 'project_billing'
  );
}

export function OwnerBillingDetail() {
  const { projectVuid, recordVuid } = useParams();
  const [projectName, setProjectName] = useState('');
  const [billing, setBilling] = useState(null);
  const [lines, setLines] = useState([]);
  const [feeLevel, setFeeLevel] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [periodVuid, setPeriodVuid] = useState('');
  const [posted, setPosted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [flash, setFlash] = useState(null);
  const [codeMap, setCodeMap] = useState({});

  const loadRecord = async () => {
    const [billingData, linePayload, periodPayload, project] = await Promise.all([
      getJson(`/api/project-billings/${recordVuid}`),
      getJson(`/api/project-billings/${recordVuid}/line-items`).catch(() => []),
      getJson('/api/accounting-periods').catch(() => []),
      projectVuid ? getJson(`/api/projects/${projectVuid}`).catch(() => null) : Promise.resolve(null),
    ]);
    const currentPeriodVuid = billingData.accounting_period_vuid || billingData.accounting_period?.vuid || '';
    const periodList = sortPeriods(asList(periodPayload));
    if (billingData.accounting_period?.vuid && !periodList.some((period) => period.vuid === billingData.accounting_period.vuid)) {
      periodList.unshift(billingData.accounting_period);
    }
    setBilling(billingData);
    const nextLines = asList(linePayload).length ? asList(linePayload) : (billingData.line_items || []);
    setLines(nextLines);
    const lookups = await loadCostLookups(projectVuid, costCodeVuidsFrom(nextLines));
    setCodeMap(lookups.codeMap || {});
    const contractVuid = billingData.contract_vuid || billingData.contract?.vuid;
    if (contractVuid) {
      const feeData = await getJson(`/api/contracts/${contractVuid}/fee-level`).catch(() => null);
      setFeeLevel(feeData);
    } else {
      setFeeLevel(null);
    }
    setPeriods(periodList);
    setPeriodVuid(currentPeriodVuid);
    setPosted(await billingIsPosted(projectVuid, recordVuid, currentPeriodVuid));
    if (project?.project_name) setProjectName(project.project_name);
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    loadRecord()
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.error || 'Unable to load this record');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectVuid, recordVuid]);

  const selectedPeriod = periods.find((period) => period.vuid === periodVuid);
  const approved = String(billing?.status || '').toLowerCase() === 'approved';
  const periodOpen = selectedPeriod?.status === 'open';
  const title = billing?.billing_number || billing?.application_number || 'Owner Billing';
  const parentTo = `/procore/projects/${projectVuid}/invoicing`;

  const handlePeriodChange = async (nextPeriodVuid) => {
    if (!nextPeriodVuid || nextPeriodVuid === periodVuid || posted) return;
    setBusy('period');
    setFlash(null);
    try {
      await putJson(`/api/project-billings/${recordVuid}`, { accounting_period_vuid: nextPeriodVuid });
      setPeriodVuid(nextPeriodVuid);
      setBilling((current) => ({
        ...current,
        accounting_period_vuid: nextPeriodVuid,
        accounting_period: periods.find((period) => period.vuid === nextPeriodVuid) || current?.accounting_period,
      }));
      setPosted(await billingIsPosted(projectVuid, recordVuid, nextPeriodVuid));
      setFlash({ type: 'ok', text: 'Accounting period saved.' });
    } catch (err) {
      setFlash({ type: 'err', text: integrationError(err) });
    } finally {
      setBusy('');
    }
  };

  const handlePost = async () => {
    if (posted || !approved || !periodVuid || !periodOpen) return;
    setBusy('post');
    setFlash(null);
    try {
      const data = await postJson('/api/posted-records/post', {
        transaction_type: 'project_billing',
        transaction_vuid: recordVuid,
        posted_by: 'User',
      });
      setPosted(true);
      setFlash({ type: 'ok', text: data?.message || 'Posted to the general ledger.' });
    } catch (err) {
      setFlash({ type: 'err', text: integrationError(err) });
    } finally {
      setBusy('');
    }
  };

  if (loading) {
    return <div className="pc-page"><div className="pc-empty">Loading record…</div></div>;
  }
  if (error || !billing) {
    return <div className="pc-page"><div className="pc-empty">{error || 'Record not found'}</div></div>;
  }

  const postDisabled = Boolean(busy) || posted || !approved || !periodVuid || !periodOpen;
  const postTitle = posted
    ? 'Already posted'
    : !approved
      ? 'Only approved billings can be posted'
      : !periodVuid
        ? 'Choose an accounting period before posting'
        : !periodOpen
          ? 'Journal entries can only be posted to an open period'
          : 'Post this billing to the general ledger';

  return (
    <div className="pc-page">
      <div className="pc-crumb">
        <Link to="/procore">Portfolio</Link>
        {' / '}
        <Link to={`/procore/projects/${projectVuid}`}>{projectName || 'Project'}</Link>
        {' / '}
        <Link to={parentTo}>Invoicing</Link>
        {' / '}
        {title}
      </div>
      <div className="pc-title-row">
        <h1>{title}</h1>
        <div className="pc-title-actions">
          {posted ? <span className={`pc-pill ${statusClass('posted')}`}>posted</span> : null}
          {billing.status ? (
            <span className={`pc-pill ${statusClass(billing.status)}`}>{billing.status}</span>
          ) : null}
          {!posted ? (
            <button
              type="button"
              className="pc-btn"
              disabled={postDisabled}
              title={postTitle}
              onClick={handlePost}
            >
              {busy === 'post' ? 'Posting…' : 'Post'}
            </button>
          ) : null}
          <IntegrationActions
            projectVuid={projectVuid}
            sendKind="project-billing"
            sendPayload={{
              project_billing_vuid: recordVuid,
              ...(billing.contract?.vuid ? { contract_vuid: billing.contract.vuid } : {}),
            }}
          />
        </div>
      </div>
      {flash ? (
        <div className={`pc-flash ${flash.type === 'ok' ? 'pc-flash-ok' : 'pc-flash-err'}`}>{flash.text}</div>
      ) : null}
      <div className="pc-card">
        <div className="pc-kv">
          <div>
            <div className="pc-label">Period</div>
            <div className="pc-value">
              <select
                className="pc-select"
                value={periodVuid}
                disabled={posted || Boolean(busy)}
                aria-label="Accounting period"
                onChange={(event) => handlePeriodChange(event.target.value)}
              >
                {!periodVuid ? <option value="">Select a period</option> : null}
                {periods.map((period) => (
                  <option
                    key={period.vuid}
                    value={period.vuid}
                    disabled={period.status !== 'open' && period.vuid !== periodVuid}
                  >
                    {periodLabel(period)}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <div className="pc-label">Billing Date</div>
            <div className="pc-value">{shortDate(billing.billing_date || billing.application_date)}</div>
          </div>
          <div>
            <div className="pc-label">Total</div>
            <div className="pc-value">{money(billing.total_amount || billing.current_payment_due)}</div>
          </div>
          {feeLevel?.fee_level ? (
            <div>
              <div className="pc-label">Fee Method</div>
              <div className="pc-value">{feeLevelLabel(feeLevel.fee_level)}</div>
            </div>
          ) : null}
        </div>
        <div className="pc-panel-h">SOV lines</div>
        {!lines.length ? (
          <div className="pc-empty">No line items on this record.</div>
        ) : (
          <table className="pc-table">
            <thead>
              <tr>
                <th>Description</th>
                {usesLineMarkups(feeLevel?.fee_level) ? <th className="pc-right">Markup %</th> : null}
                <th className="pc-right">This Period</th>
                <th className="pc-right">Completed</th>
                <th className="pc-right">Retainage</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => {
                const codeLabel = costCodeLabel(line, codeMap);
                const thisPeriod = line.actual_billing_amount ?? line.billing_amount ?? line.total_cost
                  ?? line.work_completed_this_period ?? line.this_period_amount;
                const completed = line.total_completed ?? line.work_completed_to_date
                  ?? line.actual_billing_amount ?? line.total_cost;
                const retainage = line.retention_held ?? line.retainage_held ?? line.retainage_amount;
                const isFeeLine = usesFeeCostCodeLine(feeLevel?.fee_level)
                  && line.cost_code_vuid === feeLevel?.fee_cost_code_vuid
                  && line.cost_type_vuid === feeLevel?.fee_cost_type_vuid;
                const description = codeLabel !== '—' ? codeLabel : (line.description || '—');
                return (
                  <tr key={line.vuid}>
                    <td>
                      {description}
                      {isFeeLine ? <div className="pc-sub">Fee line</div> : null}
                    </td>
                    {usesLineMarkups(feeLevel?.fee_level) ? (
                      <td className="pc-right">
                        {isFeeLine ? '—' : `${Number(line.markup_percentage || 0).toFixed(1)}%`}
                      </td>
                    ) : null}
                    <td className="pc-right">{money(thisPeriod)}</td>
                    <td className="pc-right">{money(completed)}</td>
                    <td className="pc-right">{money(retainage)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export function ChangeOrderDetail() {
  const { projectVuid, kind, recordVuid } = useParams();
  const parentTo = `/procore/projects/${projectVuid}/change-orders`;
  if (kind === 'commitment') {
    return <CommitmentChangeOrderDetail />;
  }
  return (
    <RecordDetailPage
      parentLabel="Change Orders"
      parentTo={parentTo}
      loader={async () => {
        const isInternal = kind === 'internal';
        const listPath = isInternal ? '/api/internal-change-orders' : '/api/external-change-orders';
        const linePath = isInternal
          ? `/api/internal-change-orders/${recordVuid}/lines`
          : `/api/external-change-orders/${recordVuid}/lines`;
        const [list, lines] = await Promise.all([
          getJson(listPath, { project_vuid: projectVuid }),
          getJson(linePath).catch(() => []),
        ]);
        const { codeMap, typeMap } = await loadCostLookups(projectVuid, costCodeVuidsFrom(asList(lines)));
        const header = asList(list).find((row) => row.vuid === recordVuid) || {};
        const headerAmount = isInternal
          ? header.total_change_amount
          : header.total_contract_change_amount ?? header.total_budget_change_amount ?? header.total_amount;
        return {
          title: `${header.change_order_number || ''} ${header.description || header.change_order_name || 'Change Order'}`.trim(),
          status: header.status,
          deleteUrl: isInternal
            ? `/api/internal-change-orders/${recordVuid}`
            : `/api/external-change-orders/${recordVuid}`,
          deleteConfirm: `Unsync this ${isInternal ? 'internal' : 'external'} change order in Procore (remove origin_id) and delete it from Claret? This cannot be undone.`,
          period: {
            accounting_period_vuid: header.accounting_period_vuid,
            saveUrl: isInternal
              ? `/api/internal-change-orders/${recordVuid}`
              : `/api/external-change-orders/${recordVuid}`,
          },
          fields: [
            { label: 'Type', value: isInternal ? 'Internal' : 'Prime / External' },
            { label: 'Number', value: header.change_order_number },
            { label: 'Amount', value: headerAmount, money: true },
            { label: 'Date', value: header.change_order_date || header.approval_date, date: true },
          ],
          lineColumns: [
            { key: 'description', label: 'Description' },
            { key: 'cost_code', label: 'Cost Code' },
            { key: 'cost_type', label: 'Cost Type' },
            { key: 'amount', label: 'Amount', money: true },
          ],
          lines: asList(lines).map((line) => ({
            id: line.vuid,
            description: line.description || line.notes,
            cost_code: costCodeLabel(line, codeMap),
            cost_type: costTypeLabel(line, typeMap),
            amount: isInternal
              ? line.change_amount
              : line.contract_amount_change ?? line.budget_amount_change ?? line.total_amount ?? line.amount,
          })),
        };
      }}
    />
  );
}

export function CommitmentChangeOrderDetail() {
  const { projectVuid, recordVuid, coNumber } = useParams();
  const number = decodeURIComponent(coNumber || '');
  return (
    <RecordDetailPage
      parentLabel="Change Orders"
      parentTo={`/procore/projects/${projectVuid}/change-orders`}
      loader={async () => {
        const [data, ccoList] = await Promise.all([
          getJson(`/api/commitments/${recordVuid}`),
          getJson('/api/commitment-change-orders', { commitment_vuid: recordVuid }).catch(() => []),
        ]);
        const lines = (data.line_items || []).filter(
          (line) => String(line.change_order_number || '') === number
        );
        const amount = lines.reduce((sum, line) => sum + Number(line.total_cost || 0), 0);
        const header = asList(ccoList).find(
          (row) => String(row.change_order_number || '') === number
        );
        return {
          title: `CCO ${number}`,
          status: header?.status || data.status,
          deleteUrl:
            header?.source === 'commitment_change_order' && header?.vuid
              ? `/api/commitment-change-orders/${header.vuid}`
              : null,
          deleteConfirm: `Unsync CCO ${number} in Procore (remove origin_id) and delete it from Claret? This cannot be undone.`,
          period: {
            accounting_period_vuid: header?.accounting_period_vuid,
            accounting_period: header?.accounting_period,
            saveUrl:
              header?.source === 'commitment_change_order' && header?.vuid
                ? `/api/commitment-change-orders/${header.vuid}`
                : null,
          },
          fields: [
            { label: 'Type', value: 'Commitment Change Order' },
            { label: 'Commitment', value: `${data.commitment_number} ${data.commitment_name || ''}`.trim() },
            { label: 'Vendor', value: data.vendor?.vendor_name },
            { label: 'Amount', value: header?.total_amount ?? amount, money: true },
          ],
          lineColumns: [
            { key: 'description', label: 'Description' },
            { key: 'quantity', label: 'Qty' },
            { key: 'unit_cost', label: 'Unit Cost', money: true },
            { key: 'total_cost', label: 'Amount', money: true },
          ],
          lines: lines.map((line) => ({
            id: line.vuid,
            description: line.description,
            quantity: line.quantity,
            unit_cost: line.unit_cost,
            total_cost: line.total_cost,
          })),
        };
      }}
    />
  );
}

export function DirectCostDetail() {
  const { projectVuid, recordVuid } = useParams();
  return (
    <RecordDetailPage
      parentLabel="Direct Costs"
      parentTo={`/procore/projects/${projectVuid}/direct-costs`}
      loader={async () => {
        const data = await getJson(`/api/project-expenses/${recordVuid}`);
        return {
          title: data.expense_number || data.description || 'Direct Cost',
          status: data.status,
          sendKind: 'direct-cost',
          sendPayload: { source_type: 'project_expense', source_vuid: recordVuid },
          fields: [
            { label: 'Vendor', value: data.vendor?.vendor_name },
            { label: 'Date', value: data.expense_date, date: true },
            { label: 'Amount', value: data.amount, money: true },
            { label: 'Cost Code', value: data.cost_code?.code },
            { label: 'Cost Type', value: data.cost_type?.cost_type || data.cost_type?.abbreviation },
            { label: 'Memo', value: data.memo || data.description },
          ],
          lineTitle: 'Details',
          lineColumns: [
            { key: 'description', label: 'Description' },
            { key: 'amount', label: 'Amount', money: true },
          ],
          lines: [
            {
              id: data.vuid,
              description: data.description || data.memo || 'Direct cost',
              amount: data.amount,
            },
          ],
        };
      }}
    />
  );
}

export function BudgetDetail() {
  const { projectVuid, recordVuid } = useParams();
  return (
    <RecordDetailPage
      parentLabel="Budget"
      parentTo={`/procore/projects/${projectVuid}/budget`}
      loader={async () => {
        const [budget, linePayload] = await Promise.all([
          getJson(`/api/project-budgets/${recordVuid}`),
          getJson(`/api/project-budgets/${recordVuid}/lines`).catch(() => []),
        ]);
        const lines = asList(linePayload).length ? asList(linePayload) : (budget.budget_lines || budget.lines || []);
        const { codeMap, typeMap } = await loadCostLookups(projectVuid, costCodeVuidsFrom(lines));
        return {
          title: budget.description || budget.budget_type || 'Budget',
          status: budget.status,
          sendKind: 'budget',
          sendPayload: { project_vuid: projectVuid, budget_vuid: recordVuid },
          fields: [
            { label: 'Type', value: budget.budget_type },
            { label: 'Date', value: budget.budget_date, date: true },
            { label: 'Total', value: budget.budget_amount, money: true },
            { label: 'Finalized', value: budget.finalized ? 'Yes' : 'No' },
          ],
          lineColumns: [
            { key: 'cost_code', label: 'Cost Code' },
            { key: 'cost_type', label: 'Cost Type' },
            { key: 'amount', label: 'Original Budget', money: true },
            { key: 'notes', label: 'Notes' },
          ],
          lines: lines.map((line) => ({
            id: line.vuid,
            cost_code: costCodeLabel(line, codeMap),
            cost_type: costTypeLabel(line, typeMap),
            amount: budgetLineAmount(line),
            notes: line.notes,
          })),
        };
      }}
    />
  );
}

export function ProjectContractDetail() {
  const { projectVuid, recordVuid } = useParams();
  return (
    <RecordDetailPage
      parentLabel="Project Contracts"
      parentTo={`/procore/projects/${projectVuid}/contracts`}
      loader={async () => {
        const [data, itemPayload, feeLevel] = await Promise.all([
          getJson(`/api/project-contracts/${recordVuid}`),
          getJson(`/api/project-contracts/${recordVuid}/items`).catch(() => []),
          getJson(`/api/contracts/${recordVuid}/fee-level`).catch(() => null),
        ]);
        const lines = asList(itemPayload).length ? asList(itemPayload) : (data.items || []);
        const { codeMap, typeMap } = await loadCostLookups(projectVuid, costCodeVuidsFrom(lines));
        const showLineMarkups = usesLineMarkups(feeLevel?.fee_level);
        return {
          title: `${data.contract_number || ''} ${data.contract_name || 'Project Contract'}`.trim(),
          status: data.status,
          sendKind: 'contract',
          sendPayload: { contract_vuid: recordVuid },
          fields: [
            { label: 'Number', value: data.contract_number },
            { label: 'Customer', value: data.customer?.customer_name || data.customer_name },
            { label: 'Type', value: data.contract_type },
            { label: 'Fee Method', value: feeLevelLabel(feeLevel?.fee_level) },
            { label: 'Amount', value: data.contract_amount, money: true },
            { label: 'Start', value: data.start_date, date: true },
            { label: 'End', value: data.end_date, date: true },
          ],
          lineTitle: 'Schedule of Values',
          lineColumns: [
            { key: 'item_number', label: '#' },
            { key: 'description', label: 'Description' },
            { key: 'cost_code', label: 'Cost Code' },
            { key: 'cost_type', label: 'Cost Type' },
            ...(showLineMarkups ? [{ key: 'markup_percentage', label: 'Markup %' }] : []),
            ...(showLineMarkups ? [{ key: 'labor_fee', label: 'Fee on Labor Schedule' }] : []),
            { key: 'total_amount', label: 'Amount', money: true },
          ],
          lines: lines.map((line) => ({
            id: line.vuid,
            item_number: line.item_number,
            description: line.description,
            cost_code: costCodeLabel(line, codeMap),
            cost_type: costTypeLabel(line, typeMap),
            markup_percentage: line.markup_percentage != null ? `${Number(line.markup_percentage || 0).toFixed(1)}%` : '—',
            labor_fee: line.apply_fee_to_labor_schedule ? 'Yes' : 'No',
            total_amount: line.total_amount,
          })),
        };
      }}
    />
  );
}

export function DirectoryDetail() {
  const { kind, recordVuid } = useParams();
  const isVendor = kind === 'vendors';
  const isCustomer = kind === 'customers';
  return (
    <RecordDetailPage
      parentLabel="Directory"
      parentTo="/procore/directory"
      loader={async () => {
        const endpoint = isVendor
          ? `/api/vendors/${recordVuid}`
          : isCustomer
            ? `/api/customers/${recordVuid}`
            : `/api/employees/${recordVuid}`;
        const data = await getJson(endpoint);
        const name = isVendor
          ? data.vendor_name || data.company_name
          : isCustomer
            ? data.customer_name || data.company_name
            : data.employee_name || `${data.first_name || ''} ${data.last_name || ''}`.trim();
        return {
          title: name || 'Directory record',
          status: data.status,
          sendKind: isVendor ? 'vendor' : undefined,
          sendPayload: isVendor ? { vendor_vuid: recordVuid } : undefined,
          fields: isVendor
            ? [
                { label: 'Number', value: data.vendor_number },
                { label: 'Company', value: data.company_name },
                { label: 'Email', value: data.email },
                { label: 'Phone', value: data.phone },
              ]
            : isCustomer
              ? [
                  { label: 'Number', value: data.customer_number },
                  { label: 'Company', value: data.company_name },
                  { label: 'Contact', value: data.contact_person },
                  { label: 'Email', value: data.email },
                  { label: 'Phone', value: data.phone },
                ]
              : [
                  { label: 'Number', value: data.employee_number },
                  { label: 'Email', value: data.email },
                  { label: 'Title', value: data.job_title || data.title },
                ],
          lineTitle: 'Contact',
          lineColumns: [
            { key: 'label', label: 'Field' },
            { key: 'value', label: 'Value' },
          ],
          lines: [
            { id: '1', label: 'Name', value: name },
            { id: '2', label: 'Status', value: data.status },
          ],
        };
      }}
    />
  );
}
